/**
 * Markdown-to-Word formatter — public API.
 *
 * Orchestrates the three layers:
 *   1. Config (what styles to apply)
 *   2. Parser (what the text is)
 *   3. Renderer (how to write it)
 *
 * Single entry point: formatAndInsert()
 */

/* global Word */

import { mergeConfig } from "./formatterConfig";
import type { FormatterConfig } from "./formatterConfig";
import { assignSubheadingLevels, parseMarkdown } from "./markdownParser";
import { insertPendingImages, pendingImages, renderNodes } from "./wordRenderer";
import { currentDocEpoch } from "../liveDocLoadState";
import { ASTHRA_FIGURE_ALT_TITLE } from "../sectionMarkdown";
import { enqueueWordWrite } from "../wordWriteQueue";
import { reportWordFailure } from "../wordOpsLogger";

/** The run whose writer-inserted figures a caption may resolve to, and how to
 *  authenticate for them. Set by the shell when a run is loaded / active. */
let figureScope: { runId: string; getToken: () => string | null } | null = null;

/** Point caption resolution at a run: ``[IMAGE DATA: <caption>]`` then tries the
 *  run's own figures (pictures a writer inserted and saved) before the source
 *  library. ``null`` clears it. */
export function setFigureRunScope(runId: string | null, getToken?: () => string | null): void {
  figureScope = runId ? { runId, getToken: getToken ?? (() => null) } : null;
}

const RE_FIND_IMAGE_CAPTION = /\/api\/find-image\?caption=([^&]+)/;

/** The caption behind a ``/api/find-image?caption=…`` URL, or null. */
function captionOfFindImageUrl(url: string): string | null {
  const m = RE_FIND_IMAGE_CAPTION.exec(url);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

// Re-export types that consumers may need
export type { FormatterConfig } from "./formatterConfig";
export type { ParsedNode, InlineSegment } from "./types";
export { parseMarkdown, needsFormatterRender } from "./markdownParser";
export { DEFAULT_CONFIG } from "./formatterConfig";

/** The section heading's outline level: from its loaded style when the caller
 *  loaded it (``Heading3`` → 3), else the depth of its number (``2.6.3`` → 3). */
export function sectionHeadingLevel(headingParagraph: Word.Paragraph, sectionNumber: string): number {
  try {
    const m = /heading\s*([1-9])/i.exec(`${headingParagraph.styleBuiltIn ?? ""} ${headingParagraph.style ?? ""}`);
    if (m) return Number(m[1]);
  } catch {
    // style not loaded on this proxy — fall back to the number
  }
  return Math.max(1, sectionNumber.split(".").filter(Boolean).length);
}

/**
 * Parse markdown content and insert formatted paragraphs into Word.
 *
 * This is the single function that replaces the naive line-by-line
 * insertion in wordWriter.ts. It handles:
 * - Section heading deduplication (skip parent, keep children, stop at boundary)
 * - Markdown headings → Word heading one level below the section, unnumbered
 * - Bold, [NOT FOUND], [REVIEWER:], [FILL:] → run-level formatting
 * - Markdown tables → Word tables
 * - Times New Roman 11pt on every run (not just paragraph-level)
 * - Justified alignment, proper spacing
 *
 * @param context - The Word request context (caller's Word.run scope) — used
 *                  to sync between every node insertion so the proxy chain
 *                  doesn't silently drop everything past the first paragraph.
 * @param headingParagraph - The scaffold heading paragraph to insert content after
 * @param markdownContent - The agent's raw markdown output
 * @param sectionNumber - The parent section number (e.g. "9.2") for heading dedup
 * @param configOverrides - Optional partial config to override DEFAULT_CONFIG
 */
export async function formatAndInsert(
  context: Word.RequestContext,
  headingParagraph: Word.Paragraph,
  markdownContent: string,
  sectionNumber: string,
  configOverrides?: Partial<FormatterConfig>,
): Promise<void> {
  const config = mergeConfig(configOverrides);

  // In demo mode, normalize [NOT FOUND - ...] markers to clean [TO FILL].
  // In production mode, keep the detailed markers with highlights.
  let contentToFormat = markdownContent;
  if (config.demoMode) {
    contentToFormat = markdownContent
      .replace(/\[NOT FOUND\s*-\s*(?:NOT IN SOURCE|RETRIEVAL FAILURE|AWAITING DOCUMENT)(?::\s*[^\]]+)?\]/g, "[TO FILL]")
      .replace(/\[NOT FOUND\s*-\s*[^\]]+\]/g, "[TO FILL]");
  }
  // Subheadings take heading styles only under a known section heading; the
  // span splices (sectionNumber "", anchored on a body paragraph) keep them as
  // bold body paragraphs rather than guess an outline level.
  const parsed = parseMarkdown(contentToFormat, sectionNumber);
  const nodes = sectionNumber
    ? assignSubheadingLevels(parsed, sectionHeadingLevel(headingParagraph, sectionNumber), sectionNumber)
    : parsed;

  if (nodes.length === 0) {
    console.warn(`[formatter] No content nodes parsed for §${sectionNumber}`);
    return;
  }

  const epoch = currentDocEpoch();
  await renderNodes(context, headingParagraph, nodes, config);
  if (currentDocEpoch() !== epoch) {
    // The body was rebuilt mid-render; this section's placeholders are gone.
    pendingImages.length = 0;
    return;
  }

  // Capture pending images BEFORE they're cleared — we'll process them
  // in a separate Word.run after this one completes. This is critical:
  // inserting images inside the caller's Word.run would exceed its
  // timeout on sections with many images (29 in section 3).
  const imagesToInsert = [...pendingImages];
  pendingImages.length = 0; // Clear for next section

  // Schedule image insertion as a SEPARATE async operation.
  // Text content is committed by the caller's Word.run context.sync().
  // Images are inserted afterwards in their own Word.run calls —
  // one per image for isolation (a failed image doesn't block others).
  if (imagesToInsert.length > 0) {
    // We can't use the caller's context (it may be closed after sync).
    // Instead, queue a microtask that runs AFTER the caller's Word.run
    // completes. Each image gets its own Word.run for resilience.
    setTimeout(async () => {
      let inserted = 0;
      for (const { url, alt, inCell } of imagesToInsert) {
        try {
          // Through the single-writer queue: this second pass matches its
          // placeholder anywhere in the body, so it must not interleave with a
          // Load or a section re-projection.
          await enqueueWordWrite(() => _insertSingleImage(url, alt, config, { inCell }));
          inserted++;
        } catch (err) {
          reportWordFailure("formatter.imageInsert", err);
          console.warn(`[formatter] Image insert failed for ${url}:`, err);
        }
      }
    }, 500); // Small delay to let the text Word.run complete first
  }
}

/**
 * Insert a single image into the Word document.
 *
 * Runs in its OWN Word.run context — isolated from the text insertion.
 * Finds the placeholder paragraph by searching for "[Image: {alt}]" text,
 * then replaces it with the actual image.
 *
 * If the image fetch fails, the placeholder text is updated to show
 * a failure message (graceful degradation).
 */
export async function _insertSingleImage(
  url: string,
  alt: string,
  config?: FormatterConfig,
  opts?: { inCell?: boolean },
): Promise<void> {
  const imgConfig = config?.image ?? {
    fetchTimeout: 5000,
    failurePlaceholder: "[Image could not be loaded]",
    captionColor: "#808080",
  };
  const fontName = config?.font?.name ?? "Times New Roman";

  const apiBase = (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE || "/api";

  const fullUrl = url.startsWith("/api/")
    ? url.replace("/api/", `${apiBase}/`)
    : url.startsWith("http")
      ? url
      : `${apiBase}/${url}`;

  // A caption pointer may name a picture the writer inserted and saved — ask
  // the run's own figures first, then the source library.
  const caption = captionOfFindImageUrl(url);
  const scope = figureScope;
  const candidates: Array<{ href: string; headers?: Record<string, string> }> = [];
  if (caption && scope) {
    const token = scope.getToken();
    candidates.push({
      href: `${apiBase}/runs/${encodeURIComponent(scope.runId)}/figures?caption=${encodeURIComponent(caption)}`,
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    });
  }
  candidates.push({ href: fullUrl });

  // Fetch image with timeout.
  //
  // credentials: "include" — required symmetry with the first-pass
  // insertPendingImages fetch. Without it, Word's cross-origin
  // iframe context omits the JWT cookie + some browsers refuse the
  // cookieless cross-origin request outright (images silently fail;
  // placeholder stays in doc). Images are also in the server's JWT
  // allow-list (server/main.py::jwt_guard_middleware), so this is
  // belt-and-suspenders.
  let base64: string;
  try {
    let resp: Response | null = null;
    for (const c of candidates) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), imgConfig.fetchTimeout);
      try {
        resp = await fetch(c.href, {
          signal: controller.signal,
          credentials: "include",
          ...(c.headers ? { headers: c.headers } : {}),
        });
      } catch (err) {
        resp = null;
        if (c.href === fullUrl) throw err;
      } finally {
        clearTimeout(timer);
      }
      if (resp?.ok) break;
    }
    if (!resp) return;

    if (!resp.ok) {
      console.warn(`[formatter] Image fetch failed: ${fullUrl} (${resp.status})`);
      return;
    }

    const blob = await resp.blob();
    base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] || "");
      };
      reader.readAsDataURL(blob);
    });

    if (!base64) return;
  } catch (err) {
    console.warn(`[formatter] Image fetch error for ${url}:`, err);
    return;
  }

  // Insert into Word in its own context
  await Word.run(async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    // Find the placeholder paragraph by matching the [Image: prefix.
    // Use includes() for resilience — Word may modify whitespace,
    // encode special characters (em dashes, etc.), or add trailing
    // characters after context.sync(). Match on the first 30 chars
    // of the alt text to avoid false positives while being tolerant.
    const matchKey = alt.slice(0, 30);
    for (let i = 0; i < paragraphs.items.length; i++) {
      const paraText = paragraphs.items[i].text.trim();
      if (paraText.startsWith("[Image:") && paraText.includes(matchKey)) {
        const para = paragraphs.items[i];
        para.clear();
        para.spaceAfter = 4;
        para.spaceBefore = 4;
        para.alignment = "Centered";
        const picture = para.insertInlinePictureFromBase64(base64, "Start");
        // Constrain the width. Body figures cap at 5 inches (360 pt); a figure
        // embedded in a table cell (e.g. the CMC Photograph Comparison table)
        // caps at 2 inches (144 pt) so a single thumbnail doesn't blow the
        // column out to full width. lockAspectRatio keeps tall/narrow images
        // from consuming a full page / row.
        picture.width = opts?.inCell ? 144 : 360;
        picture.lockAspectRatio = true;
        // Record which pointer this figure came from, so a later Save writes it
        // back as that pointer instead of losing it (sectionMarkdown.ts).
        if (caption) {
          picture.altTextTitle = ASTHRA_FIGURE_ALT_TITLE;
          picture.altTextDescription = caption;
        } else if (url) {
          picture.altTextTitle = `${ASTHRA_FIGURE_ALT_TITLE}|${url}`;
          picture.altTextDescription = alt;
        }

        // Caption below — tight spacing to keep it visually attached. Skipped
        // for in-cell figures: the comparison row already labels the cell, and
        // a caption paragraph would just add height inside the cell.
        if (alt && !opts?.inCell) {
          const caption = para.insertParagraph(alt, "After");
          caption.font.name = fontName;
          caption.font.size = 9;
          caption.font.italic = true;
          caption.font.color = imgConfig.captionColor;
          caption.alignment = "Centered";
          caption.spaceAfter = 6;
          caption.spaceBefore = 2;
        }

        await context.sync();
        return;
      }
    }
  });
}
